import {AfterViewInit, ChangeDetectorRef, Component, ViewChild, ViewContainerRef} from '@angular/core';
import {ICellEditorAngularComp} from "ag-grid-angular";
import {GridApi, ICellEditorParams} from "ag-grid-community";
import {DetectMethodChanges, DxValueChanged, isValidNumber, isVoid} from "../../../utils";
import {ToastrService} from "ngx-toastr";
import {OptionExpirationDescriptor} from "../../../shell-communication/shell-dto-protocol";
import {OptionsChainService} from "../../../option-chains.service";
import {HedgeMatrixRow, HgHedgeMatrixComponent} from "../hg-hedge-matrix.component";
import {ApgDataService} from "../../../adjustment-pricing-grid/services/apg-data.service";
import {HedgeData} from "../hedge-data";
import {getExpirationCellDataIndex} from "../hedge-matrix-grid-options";
import { DxPopupComponent} from "devextreme-angular";
import {HedgePosition} from "../../data-model/hedge-position";
import {HedgeMatrixCellData} from "../hedge-matrix-cell.data";
import {OrderLegWrapper} from "../../../adjustment-strategy-dialog/adjustment-strategy-dialog.model";

type CustomParams = {
    etsComponent: HgHedgeMatrixComponent;
    fieldEdited: 'transQty' | 'outcomeQty'
}

class HedgeLegWrapper {
    constructor(private readonly _hedgeLeg: HedgePosition) {
    }

    isSelected = false;

    get originalLeg(): HedgePosition {
        return this._hedgeLeg;
    }

    get ticker(): string {
        return this._hedgeLeg.ticker;
    }

    get strike(): number {
        return this._hedgeLeg.strike;
    }

    get expiration(): string {
        return this._hedgeLeg.expiration;
    }

    get qty(): number {
        return this._hedgeLeg.qty;
    }
}

export interface OtherLegWrapper {
    ticker: string;
    qty: number;
    expiration: string;
    strike: number;
    isSelected: boolean;
}

@Component({
    selector: 'ets-hg-matrix-punch-pad',
    templateUrl: './hg-matrix-punch-pad.component.html',
    styleUrls: ['./hg-matrix-punch-pad.component.scss'],
})
export class HgMatrixPunchPadComponent implements ICellEditorAngularComp, AfterViewInit {

    constructor(
        private readonly _changeDetector: ChangeDetectorRef,
        private readonly _toastr: ToastrService,
        private readonly _optionsChainService: OptionsChainService,
        private readonly _apgDataService: ApgDataService
    ) {
    }

    private _params: { api?: GridApi, etsComponent?: HgHedgeMatrixComponent } = {};

    private _originalQty: number;
    private _originalExpiration: string;
    private _fieldEdited: 'transQty' | 'outcomeQty';

    @ViewChild(DxPopupComponent) popupCmp: DxPopupComponent;

    selectedExpiration: OptionExpirationDescriptor;

    expirationList: OptionExpirationDescriptor[] = [];

    side: 'Long' | 'Short';

    qty: number;

    rollOthers: boolean = false;

    rollAll: boolean = false;

    addNewLeg = false;

    amendOthers = false;

    isCancel = false;

    hedgeData: HedgeData;

    legs: OtherLegWrapper[] = [];

    cellData: HedgeMatrixCellData;

    title: string;

    rowData: HedgeMatrixRow;

    get isAllLegsSelected(): boolean {
        return !isVoid(this.legs) && this.legs.every(x => x.isSelected);
    }

    get showRollRow(): boolean {
        return !isVoid(this._originalExpiration)
            && this._fieldEdited !== 'transQty'
            && this.selectedExpiration?.optionExpirationDate !== this._originalExpiration;
    }


    get showAmendRow(): boolean {
        return !isVoid(this._originalExpiration)
            && this._fieldEdited === 'transQty'
            && this.selectedExpiration?.optionExpirationDate !== this._originalExpiration;
    }

    get otherLegsSectionHeaderText(): string {
        return this._fieldEdited === 'transQty' ? 'Amend Other Legs' : 'Roll Other Legs';
    }

    get showOtherLegsPanel() : boolean {
        return this._fieldEdited !== 'transQty' ? this.rollOthers : this.showAmendRow && this.amendOthers;
    }

    get canAddNew() : boolean {
        if (this._fieldEdited === 'transQty') {
            return  this.showAmendRow && !this.amendOthers;
        }

        return this.showRollRow && !this.rollOthers && !this.rollAll;
    }

    get canAmendOthers() :boolean {
        return this.showAmendRow && !this.addNewLeg;
    }

    get canRollOthers() : boolean {
        return this.showRollRow && !this.addNewLeg && !this.rollAll;
    }

    get canRollAll() : boolean {
        return this.showRollRow && !this.addNewLeg && !this.rollOthers;
    }

    // don't use afterGuiAttached for post gui events - hook into ngAfterViewInit instead for this
    ngAfterViewInit() {
    }

    isEditedLeg(leg: HedgeLegWrapper) {
        return leg?.ticker === this.cellData?.ticker;
    }

    agInit(params: ICellEditorParams & CustomParams): void {

        this._originalQty = undefined;
        this._fieldEdited = params.fieldEdited;
        this.qty = undefined;
        this.selectedExpiration = undefined;
        this.expirationList = [];
        this.side = undefined;
        this.rollAll = false;

        if (isValidNumber(params.value)) {
            this._originalQty = params.value;
            if (params.value < 0) {
                this.side = 'Short';
            } else if (params.value > 0) {
                this.side = 'Long';
            }
        }

        this.qty = this._originalQty;

        this._params = params;

        const colId = params.column.getColId();
        const hedgeId = colId.startsWith('hedge:') ? colId.split(':')[2] : undefined;

        const hedgeData = this._params.etsComponent.hedgeMatrixDataService.getHedge(
            hedgeId
        );

        if (isVoid(hedgeData)) {
            throw new Error('Hedge data not defined');
        }

        this.hedgeData = hedgeData;

        const hedgeUnderlying = hedgeData.underlying;
        const cachedChain = hedgeUnderlying
            ? this._params.etsComponent.hedgeMatrixDataService.getChainForUnderlying(hedgeUnderlying)
            : undefined;

        const chainPromise = cachedChain
            ? Promise.resolve(cachedChain)
            : hedgeUnderlying
                ? this._optionsChainService.getChain(hedgeUnderlying)
                : this._apgDataService.getUnderlyingOfPortfolio(
                    this._params.etsComponent.selectedPortfolio
                ).then((data) => this._optionsChainService.getChain(data));

        chainPromise.then(data => {

            this.expirationList = data.expirations;

            const rowData = params.data as HedgeMatrixRow;

            if (isVoid(rowData)) {
                throw new Error('Row data not defined');
            }

            this.rowData = rowData;

            let expiration: string;

            const cellData = this._params.etsComponent.hedgeMatrixDataService.getCellData(
                rowData.strike,
                hedgeId,
            );

            if (!isVoid(cellData)) {

                const expirationCellDataIndex = getExpirationCellDataIndex(
                    rowData.strike,
                    params.api,
                    params.node
                );

                const expirationCellData = cellData[expirationCellDataIndex];
                this.cellData = expirationCellData;

                if (isVoid(expirationCellData)) {
                    let defaultExpiration = this._params.etsComponent.hedgeMatrixDataService
                        .getHedgeDefaultExpiration(hedgeId);
                    expiration = defaultExpiration;
                } else {
                    expiration = expirationCellData.expiration;

                    this._originalExpiration = expiration;
                }
            } else {
                let defaultExpiration = this._params.etsComponent.hedgeMatrixDataService
                    .getHedgeDefaultExpiration(
                        hedgeId
                    );
                expiration = defaultExpiration;
            }

            const optionExpirationDescriptor = this.expirationList
                .find(exp => exp.optionExpirationDate === expiration)
                            || this.expirationList[0];

            this.selectedExpiration = optionExpirationDescriptor;

            this.setTitle();

            this.popupCmp.visible = true;

            this._changeDetector.detectChanges();
        });
    }

    getValue(): any {
        const expiration = this.selectedExpiration?.optionExpirationDate || this._originalExpiration;
        const qty = this.qty;
        const changeOthers = this.rollOthers || this.rollAll || this.amendOthers;
        const addNewLeg = this.addNewLeg;
        const isCancel = this.isCancel;

        if (expiration !== this._originalExpiration || this.qty !== this._originalQty) {
            this.hedgeData.template = undefined;
            this.hedgeData.anchorExpiration = undefined;
        }

        if (changeOthers) {
            const otherLegs = this.legs
                .filter(x => x.isSelected)
                .filter(x => x.ticker !== this.cellData.ticker);

            if (this._fieldEdited === 'transQty') {
                this._params.etsComponent.amendOtherLegs(expiration,otherLegs, this.hedgeData);
            } else {
                this._params.etsComponent.rollOtherLegs(expiration, otherLegs, this.hedgeData);
            }
        }

        return {
            expiration,
            qty,
            addNewLeg,
            isCancel
        };
    }

    isPopup(): boolean {
        return true;
    }

    onQtySelected(value: number) {
        if (isVoid(this.side)) {
            this._toastr.error('Trade Side Not Selected');
            return;
        }

        if (this.side === 'Short') {
            value *= -1;
        }

        this.qty = value;

        this._params?.api?.stopEditing();
    }

    getPopupPosition() {
        return 'under';
    }

    @DetectMethodChanges()
    setSide(side: 'Long' | 'Short') {
        this.side = side;
        if (side === 'Short') {
            this.qty = Math.abs(this.qty) * -1;
        } else if (side === 'Long') {
            this.qty = Math.abs(this.qty);
        }
    }

    @DetectMethodChanges()
    onMinusClicked(num: number) {
        const current = isValidNumber(this.qty) ? this.qty : 0;
        const desired = current - num;
        this.qty = desired;
        this.updateSide();
    }

    @DetectMethodChanges()
    onPlusClicked(num: number) {
        const current = isValidNumber(this.qty) ? this.qty : 0;
        const desired = current + num;
        this.qty = desired;
        this.updateSide();
    }

    onApplyClicked() {
        try {
            this._params?.api?.stopEditing();
        } catch {
            //
        }
    }

    onCancelClicked() {
        this.qty = this._originalQty;
        this.selectedExpiration = null;
        this.isCancel = true;
        this._params.api?.stopEditing();
    }

    @DetectMethodChanges()
    reversePosition() {
        if (!isValidNumber(this.qty, true)) {
            return;
        }
        this.qty = this.qty * -1;
        this.updateSide();
    }

    @DetectMethodChanges()
    closePosition() {
        if (!isValidNumber(this.qty, true)) {
            return;
        }
        this.qty = undefined;
        this.updateSide();
    }

    @DetectMethodChanges()
    doublePosition() {
        if (!isValidNumber(this.qty, true)) {
            return;
        }
        this.qty = this.qty * 2;
        this.updateSide();
    }

    @DetectMethodChanges()
    halfPosition() {
        if (!isValidNumber(this.qty, true)) {
            return;
        }
        this.qty = Math.floor(this.qty / 2);
        this.updateSide();
    }

    @DetectMethodChanges()
    updateSide() {
        if (this.qty > 0) {
            this.side = 'Long';
        } else if (this.qty < 0) {
            this.side = 'Short';
        } else {
            this.side = undefined;
        }
    }

    onQtyChanged(args: DxValueChanged<number>) {
        if (isVoid(args.event)) {
            return;
        }
        this.updateSide();
    }

    @DetectMethodChanges()
    onExpirationChanged($event: any) {

    }

    @DetectMethodChanges()
    onHidden() {
        this.onCancelClicked();
    }

    @DetectMethodChanges()
    onRollOthersChanged() {
        if (!this.rollOthers) {
            this.legs = [];
            return;
        }

        this.populateRollLegs();

        const selectedLeg = this.legs.find(x => x.ticker === this.cellData.ticker);

        if (!isVoid(selectedLeg)) {
            selectedLeg.isSelected = true;
        }
    }

    @DetectMethodChanges()
    onRollAllChanged() {
        if (!this.rollAll) {
            this.legs = [];
            return;
        }

        this.populateRollLegs();
        this.legs.forEach(x => x.isSelected = true);
    }

    private populateRollLegs() {
        const cellDataForHedge = this._params.etsComponent.hedgeMatrixDataService.getCellDataForHedge(this.hedgeData.id);

        const outcomeLegs = cellDataForHedge.filter(cd => isValidNumber(cd.outcomeQty))
            .map(cd => {
                return {
                    qty: cd.outcomeQty,
                    expiration: cd.expiration,
                    ticker: cd.ticker,
                    groupId: this.hedgeData.id,
                    strike: cd.strike
                };
            });

        this.legs = outcomeLegs.map(x => {
            const otherLegWrapper: OtherLegWrapper = {
                isSelected: false,
                expiration: x.expiration,
                qty: x.qty,
                strike: x.strike,
                ticker: x.ticker
            };
            return otherLegWrapper;
        });
    }

    @DetectMethodChanges()
    onAddNewChanged() {
        this.rollAll = false;
        this.legs = [];
    }

    @DetectMethodChanges()
    onAmendOthersChanged() {
        if (!this.amendOthers) {
            this.legs = [];
            return;
        }

        const cellDataForHedge = this._params.etsComponent.hedgeMatrixDataService.getCellDataForHedge(
            this.hedgeData.id
        );


        const legs = cellDataForHedge.map(x => {
            const qty = x.transQty;
            const expiration = x.expiration;
            const ticker = x.ticker;
            const groupId = this.hedgeData.id;
            const strike = x.strike;

            const res: HedgePosition = {
                ticker,
                qty,
                expiration,
                groupId,
                strike,
                type: x.optionType
            };

            return res;
        });


        //#region Amend others new style
        // const cellDataForHedge = this._params.etsComponent.hedgeMatrixDataService.getCellDataForHedge(this.hedgeData.id);
        //
        // const transLegs = cellDataForHedge.filter(cd => isValidNumber(cd.transQty, true))
        //     .map(cd => {
        //         return {
        //             ticker: cd.ticker,
        //             qty: cd.transQty,
        //             expiration: cd.expiration,
        //             groupId: this.hedgeData.id,
        //             strike: cd.strike
        //         };
        //     });
        //
        // this.legs = transLegs.map(x => {
        //     const otherLegWrapper: OtherLegWrapper = {
        //         isSelected: false,
        //         expiration: x.expiration,
        //         qty: x.qty,
        //         strike: x.strike,
        //         ticker: x.ticker
        //     };
        //     return otherLegWrapper;
        // });
        //#endregion


        this.legs = legs.map(x => new HedgeLegWrapper(x));

        const selectedLeg = this.legs.find(x => x.ticker === this.cellData.ticker);

        if (!isVoid(selectedLeg)) {
            selectedLeg.isSelected = true;
        }
    }

    setTitle() {
        let qty: number;

        switch (this._fieldEdited) {
            case "transQty":
                qty = this.cellData?.transQty;
                break;
            case "outcomeQty":
                qty = this.cellData?.outcomeQty;
                break;
            default:
                qty = this.cellData?.qty;
        }



        let title = `${this.hedgeData?.name} - ${this.rowData?.strike}`;

        if (isValidNumber(qty)) {
            const sign = qty > 0 ? '+' : '';
            title += ` (${sign}${qty})`;
        }

        this.title = title;
    }

    @DetectMethodChanges()
    toggleSelectAllLegs(args: DxValueChanged<boolean>) {

        if (isVoid(args.event)) {
            return;
        }

        const sum = this.legs.map(x => x.isSelected ? 1 : 0).reduce((a, b) => a+b, 0);
        if (sum !== this.legs.length) {
            this.legs.forEach(x => x.isSelected = true);
        } else {
            this.legs
                .filter(x => x.ticker !== this.cellData?.ticker)
                .forEach(x => x.isSelected = false);
        }
    }

    getSortedLegs() {
        if (this.legs.length === 0) {
            return [];
        }

        const isCall = this.legs[0].ticker.indexOf(' Call ');

        let result = this.legs.slice();

        if (isCall) {
          result = result.reverse();
        }

        return result;
    }

    trackByFn(index: number, leg: OtherLegWrapper) {
        return leg.ticker;
    }
}